import { memo, useEffect, useRef, useState } from 'react';
import {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useTranslation } from '@bonanza-social/i18n';
import { useSelector } from '@bonanza-social/toolkit/state';
import { Box, Text } from '@bonanza-social/moonlight';
import {
  selectPrizeWallDataState,
  selectPrizeWallPrizes,
  selectPrizeWallWinners,
} from '../../state/prizeWall/prizeWall.slice';
import { PrizeWallWinnersFeedProps } from './PrizeWallWinnersFeed.types';
import PrizeWallWinnersFeedIndicator from './PrizeWallWinnersFeedIndicator/PrizeWallWinnersFeedIndicator';

function PrizeWallWinnersFeed(props: PrizeWallWinnersFeedProps) {
  const [t] = useTranslation('prizeWall');
  const winners = useSelector(selectPrizeWallWinners);
  const prizes = useSelector(selectPrizeWallPrizes);
  const dataState = useSelector(selectPrizeWallDataState);
  const [currentIndex, setCurrentIndex] = useState(0);
  const animatedValue = useSharedValue(1);
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: animatedValue.value,
  }));
  const currentWinner = winners?.[currentIndex];
  const isLastIndex = winners && currentIndex >= winners.length - 1;
  const currentPrize = currentWinner && prizes?.byId[currentWinner.rewardId];
  const winnersRef = useRef(winners ?? []);

  winnersRef.current = winners ?? [];

  useEffect(() => {
    // Reset current index on every prize wall refetch
    if (dataState === 'fulfilled') {
      setCurrentIndex(0);
    }
  }, [dataState]);

  useEffect(() => {
    if (!isLastIndex && winnersRef.current.length) {
      let timeout: NodeJS.Timeout;

      const increaseIndex = () => {
        setCurrentIndex((index) =>
          Math.max(0, Math.min(index + 1, winnersRef.current.length - 1)),
        );
      };

      const startTimer = () => {
        timeout = setTimeout(() => {
          animatedValue.value = withSequence(
            withTiming(0, { duration: 500 }, () => {
              scheduleOnRN(increaseIndex);
              scheduleOnRN(startTimer);
            }),
            withTiming(1, { duration: 500 }),
          );
        }, 3500);
      };

      startTimer();

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [animatedValue, isLastIndex]);

  return (
    <Box
      padding="space-200"
      borderRadius="radius-full"
      borderColor={
        currentPrize?.tier === 'High' ? 'borderGoldSolid' : 'borderSecondary'
      }
      borderWidth="border-width-025"
      backgroundColor="primaryBackgroundHover"
      boxShadow={[
        {
          offsetX: 0,
          offsetY: 0,
          blurRadius: 24,
          color:
            currentPrize?.tier === 'High'
              ? 'primarySolidBackground'
              : 'transparent',
        },
      ]}
      opacity={Number(currentWinner !== undefined)}
      centerContent
      {...props}
    >
      <PrizeWallWinnersFeedIndicator position="absolute" left="space-200" />
      <Text
        variant="caption"
        textAlign="center"
        animatedStyle={animatedStyle}
        {...(currentPrize?.tier === 'High'
          ? { gradient: 'darkSurfaceAccent' }
          : { color: 'darkSurfacePrimary' })}
      >
        {t('prizeWallWinnerFeedInfo', {
          playerName: currentWinner?.username,
          prizeName: currentPrize?.name,
        })}
      </Text>
    </Box>
  );
}

export default memo(PrizeWallWinnersFeed);
